import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaSave, FaChevronLeft } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import LeaseApiService from '../../services/apiService';
import AgentAttachments from './agentAttachments';
import AgentPropertyPercent from './agentPropertyPercent';
import {
  isAgentApiSuccess,
  unwrapAgentError,
  resolveBrokerId,
  pickAgentField,
  toAgentBool,
  fetchAgentDropdownsAndGrids,
  fetchAgentById,
  uploadAgentAttachments,
} from './agentMasterApiHelpers';

// Legacy cbType.Items - not sourced from the API (loaddropdownsandgrids doesn't
// return a type list at all).
const TYPE_OPTIONS = [
  { value: 'External', label: 'External' },
  { value: 'Internal', label: 'Internal' },
];

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: '#000',
    ':hover': { backgroundColor: '#F5F5F5', color: '#000 ' },
  }),
};

const emptyForm = () => ({
  brokerId: '',
  type: null,
  name: '',
  address: '',
  contactPerson: '',
  active: true,
  salesPerson: null,
  isSupplier: false,
  mobileNo: '',
  landlineNo: '',
  email: '',
  commission: '',
  intCommission: '',
  supplierId: null,
  agreementDate: null,
  limit: '',
});

const toDateOrNull = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) || d.getFullYear() <= 1900 ? null : d;
};

const AgentMasterForm = ({ mode = 'create', initialData, onClose, onSaved }) => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readOnly = isView;

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(isEdit || isView);
  const [formData, setFormData] = useState(emptyForm());
  const [attachmentsList, setAttachmentsList] = useState([]);
  const [percentageMap, setPercentageMap] = useState({});
  const [salesPersonOptions, setSalesPersonOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    fetchDropdowns();
    if (isEdit || isView) {
      loadAgent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDropdowns = async () => {
    try {
      const dropdowns = await fetchAgentDropdownsAndGrids(apiService);
      setSalesPersonOptions(dropdowns.salesPersons);
      setSupplierOptions(dropdowns.suppliers);
      // Confirmed live: the full property list ships bundled in this same call,
      // under data.propertyPercentages (all `per: 0` until an agent has rows).
      setProperties(dropdowns.properties);
    } catch (error) {
      console.error('Error fetching agent dropdown data:', error);
    }
  };

  // getbyid returns { header, attachments, propertyPercentages } - header mirrors
  // the save/update request's own field names exactly (confirmed live). The
  // getlist row (used as a fallback when brokerId can't be resolved) uses the
  // older broker_* column names instead, so both are tried.
  const populateFromAgent = (fullData) => {
    const header = fullData?.header || fullData || {};
    const typeValue = pickAgentField(header, 'type');
    const salesPersonLabel = pickAgentField(header, 'salesPerson');
    const supplierIdValue = pickAgentField(header, 'supplierId');

    setFormData({
      brokerId: resolveBrokerId(header) || resolveBrokerId(initialData) || '',
      type: typeValue ? TYPE_OPTIONS.find((o) => o.value === typeValue) || { value: typeValue, label: typeValue } : null,
      name: pickAgentField(header, 'name', 'broker_Name'),
      address: pickAgentField(header, 'address', 'broker_Address'),
      contactPerson: pickAgentField(header, 'contactPerson'),
      active: header?.active !== undefined ? toAgentBool(header.active) : true,
      salesPerson: salesPersonLabel ? { value: salesPersonLabel, label: salesPersonLabel } : null,
      isSupplier: toAgentBool(header?.isSupplier),
      mobileNo: pickAgentField(header, 'mobileNo', 'brokerMobileNo'),
      landlineNo: pickAgentField(header, 'landlineNo', 'brokerLandLineNo'),
      email: pickAgentField(header, 'email', 'brokerEmailID'),
      commission: pickAgentField(header, 'commission'),
      intCommission: pickAgentField(header, 'intCommission', 'intComm'),
      supplierId: supplierIdValue ? { value: supplierIdValue, label: supplierIdValue } : null,
      agreementDate: toDateOrNull(header?.agreementDate),
      limit: header?.limit ?? '',
    });

    const attachments = fullData?.attachments || [];
    setAttachmentsList(
      attachments.map((a) => ({
        recordId: a?.recordId ?? 0,
        description: a?.description ?? '',
        filename: a?.filename ?? '',
        docDate: toDateOrNull(a?.docDate),
        expDate: toDateOrNull(a?.expDate),
        isDeleted: false,
      })),
    );

    // propertyPercentages here is the full property list (recordId/propertyNo/
    // propertyName/per) - only keep the ones with an actual saved row/percentage.
    const propertyPercentages = fullData?.propertyPercentages || [];
    const map = {};
    propertyPercentages.forEach((p) => {
      const propertyId = String(p?.propertyNo ?? '');
      if (!propertyId || (!p?.recordId && !(Number(p?.per) > 0))) return;
      map[propertyId] = {
        recordId: p?.recordId ?? 0,
        propertyName: p?.propertyName ?? '',
        percentage: p?.per ?? 0,
      };
    });
    setPercentageMap(map);
  };

  const loadAgent = async () => {
    const brokerId = resolveBrokerId(initialData);
    if (!brokerId) {
      // Fall back to whatever the list row already had rather than blocking View/Edit entirely.
      populateFromAgent(initialData || {});
      setTableLoading(false);
      return;
    }
    try {
      setTableLoading(true);
      const fullData = await fetchAgentById(apiService, brokerId);
      populateFromAgent(fullData);
    } catch (error) {
      console.error('Error loading agent:', error);
      toast.warning(error?.message || t('agentMaster.loadFailed'));
      populateFromAgent(initialData || {});
    } finally {
      setTableLoading(false);
    }
  };

  const handleBackClick = () => {
    if (onClose) onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePercentageChange = (propertyId, propertyName, value) => {
    setPercentageMap((prev) => ({
      ...prev,
      [propertyId]: { ...(prev[propertyId] || {}), propertyName, percentage: value },
    }));
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!formData.name?.trim()) {
      toast.warning(t('agentMaster.validation.nameRequired'));
      return;
    }
    if (!formData.type) {
      toast.warning(t('agentMaster.validation.typeRequired'));
      return;
    }

    try {
      setLoading(true);
      const refNo = formData.brokerId || formData.name;
      const uploadedAttachments = await uploadAgentAttachments(refNo, attachmentsList);

      const attachments = uploadedAttachments
        .filter((a) => a.description || a.filename || a.isDeleted)
        .map((a) => ({
          recordId: a.recordId || 0,
          description: a.description || '',
          filename: a.filename || '',
          expDate: a.expDate ? new Date(a.expDate).toISOString() : null,
          docDate: a.docDate ? new Date(a.docDate).toISOString() : null,
          isDeleted: Boolean(a.isDeleted),
        }));

      const propertyPercentages = Object.entries(percentageMap)
        .filter(([, v]) => Number(v.percentage) > 0 || v.recordId)
        .map(([propertyId, v]) => ({
          recordId: v.recordId || 0,
          propertyId,
          propertyName: v.propertyName || '',
          percentage: Number(v.percentage) || 0,
        }));

      const body = {
        name: formData.name.trim(),
        address: formData.address || '',
        contactPerson: formData.contactPerson || '',
        landlineNo: formData.landlineNo || '',
        mobileNo: formData.mobileNo || '',
        email: formData.email || '',
        commission: formData.commission || '',
        intCommission: formData.intCommission || '',
        active: Boolean(formData.active),
        salesPerson: formData.salesPerson?.label || '',
        supplierId: formData.supplierId?.value != null ? String(formData.supplierId.value) : '',
        agreementDate: formData.agreementDate ? new Date(formData.agreementDate).toISOString() : null,
        isSupplier: Boolean(formData.isSupplier),
        limit: Number(formData.limit) || 0,
        type: formData.type?.value || '',
        attachments,
        propertyPercentages,
      };
      if (isEdit) {
        body.brokerId = formData.brokerId;
      }

      const res = isEdit ? await apiService.put('AGENT_MASTER_UPDATE', body) : await apiService.post('AGENT_MASTER_SAVE', body);
      if (isAgentApiSuccess(res?.data)) {
        toast.success(t(isEdit ? 'agentMaster.updateSuccess' : 'agentMaster.createSuccess'));
        if (onSaved) onSaved();
      } else {
        toast.warning(unwrapAgentError(res?.data));
      }
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error(t(isEdit ? 'agentMaster.updateFailed' : 'agentMaster.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const title = isView ? t('agentMaster.viewTitle') : isEdit ? t('agentMaster.editTitle') : t('agentMaster.addTitle');

  return (
    <div className="d-flex flex-column customer-modal-form" style={{ height: '85vh' }}>
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between flex-shrink-0">
        <div>
          <h5 className="mb-0">{title}</h5>
          {isView && <span className="text-muted small">{t('common.viewOnlyNotice')}</span>}
        </div>
        <button type="button" className="btn-close" aria-label="Close" onClick={handleBackClick}></button>
      </div>

      <div className="border-box2 mx-3 mt-3 p-2 flex-shrink-0" style={{ borderRadius: '8px' }}>
        <div className="nav nav-pills overslid d-flex gap-4">
          <div className={`dashitems sidebar-text center ${activeTab === 0 ? 'sideActive' : ''}`} onClick={() => setActiveTab(0)}>
            {t('agentMaster.tabs.details')}
          </div>
          <div className={`dashitems sidebar-text center ${activeTab === 1 ? 'sideActive' : ''}`} onClick={() => setActiveTab(1)}>
            {t('agentMaster.tabs.attachment')}
          </div>
        </div>
      </div>

      <div className="p-3" style={{ flex: 1, overflowY: 'auto', minHeight: 0, position: 'relative' }}>
        {tableLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}

        {activeTab === 0 && (
          <div className="row">
            <div className="col-lg-8">
              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {isEdit || isView ? (
                  <div className="d-flex">
                    <label className="form-label pt-1 start">{t('agentMaster.fields.id')}</label>
                    <input className="p-2 form-control w-75" value={formData.brokerId} disabled />
                  </div>
                ) : (
                  <div />
                )}
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.type')} *</label>
                  <Select
                    className="p-0 w-100"
                    isDisabled={readOnly}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.type}
                    onChange={(opt) => setFormData((prev) => ({ ...prev, type: opt }))}
                    options={TYPE_OPTIONS}
                    isClearable
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.mobileNo')}</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.mobileNo}
                    onChange={handleInputChange}
                    name="mobileNo"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.phoneNo')}</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.landlineNo}
                    onChange={handleInputChange}
                    name="landlineNo"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.name')} *</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.name}
                    onChange={handleInputChange}
                    name="name"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.emailId')}</label>
                  <input
                    type="email"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.email}
                    onChange={handleInputChange}
                    name="email"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.address')}</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.address}
                    onChange={handleInputChange}
                    name="address"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.commissionRate')}</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.commission}
                    onChange={handleInputChange}
                    name="commission"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.contactPerson')}</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    name="contactPerson"
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.intCommissionRate')}</label>
                  <input
                    type="text"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.intCommission}
                    onChange={handleInputChange}
                    name="intCommission"
                  />
                </div>
                <div className="d-flex align-items-center gap-3">
                  <label className="form-check-label">{t('agentMaster.fields.active')}</label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    style={{ width: '20px', height: '20px' }}
                    checked={formData.active}
                    disabled={readOnly}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.supplier')}</label>
                  <Select
                    className="p-0 w-100"
                    isDisabled={readOnly}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.supplierId}
                    onChange={(opt) => setFormData((prev) => ({ ...prev, supplierId: opt }))}
                    options={supplierOptions}
                    isClearable
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.salesPerson')}</label>
                  <Select
                    className="p-0 w-100"
                    isDisabled={readOnly}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.salesPerson}
                    onChange={(opt) => setFormData((prev) => ({ ...prev, salesPerson: opt }))}
                    options={salesPersonOptions}
                    isClearable
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.agreementDate')}</label>
                  <DatePicker
                    readOnly={readOnly}
                    selected={formData.agreementDate}
                    onChange={(date) => setFormData((prev) => ({ ...prev, agreementDate: date }))}
                    dateFormat="dd-MMM-yyyy"
                    className="p-2 form-control w-100"
                  />
                </div>
                <div className="d-flex align-items-center gap-3">
                  <label className="form-check-label">{t('agentMaster.fields.supplierConsultant')}</label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    style={{ width: '20px', height: '20px' }}
                    checked={formData.isSupplier}
                    disabled={readOnly}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isSupplier: e.target.checked }))}
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-1 start">{t('agentMaster.fields.limit')}</label>
                  <input
                    type="number"
                    readOnly={readOnly}
                    className="p-2 form-control w-75"
                    value={formData.limit}
                    onChange={handleInputChange}
                    name="limit"
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <AgentPropertyPercent
                properties={properties}
                percentageMap={percentageMap}
                onChangePercentage={handlePercentageChange}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}

        {activeTab === 1 && <AgentAttachments attachmentsList={attachmentsList} setAttachmentsList={setAttachmentsList} readOnly={readOnly} />}
      </div>

      <div className="p-3 border-top d-flex justify-content-start gap-3 flex-shrink-0">
        {!readOnly &&
          (loading ? (
            <div className="mainbtn flex-end gap-2">
              <ClipLoader color="#ffffff" className="mt-1" size={20} />
            </div>
          ) : (
            <div className="mainbtn flex-end gap-2" onClick={handleSave}>
              <FaSave color="#fff" className="center mt-1 mx-2" />
              <div className="center">{t('common.save')}</div>
            </div>
          ))}
        <div className={readOnly ? 'mainbtn flex-end gap-2' : 'mainbtnRed flex-end gap-2'} onClick={handleBackClick}>
          <FaChevronLeft color="#fff" className="center mt-1 mx-2" />
          <div className="center">{readOnly ? t('common.close') : t('common.cancel')}</div>
        </div>
      </div>
    </div>
  );
};

export default AgentMasterForm;
